import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StoreBadges from '@/components/StoreBadges';

function friendlyAuthError(error: any): string {
  const code: string | undefined = error?.code;
  const backendMessage: string | undefined = error?.response?.data?.message;
  if (backendMessage) return backendMessage;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled';
    case 'auth/popup-blocked':
      return 'Popup was blocked — please allow popups for this site';
    default:
      return error?.message || 'Sign-in failed. Please try again.';
  }
}

export default function LoginPage() {
  const { isAuthenticated, pendingOrganization, loginWithEmail, loginWithGoogle, loginWithApple } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'email' | 'google' | 'apple' | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      if (pendingOrganization) {
        navigate('/pending', { replace: true });
      } else {
        const from = (location.state as any)?.from?.pathname || '/chat';
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, pendingOrganization, navigate, location.state]);

  const run = async (kind: 'email' | 'google' | 'apple', fn: () => Promise<void>) => {
    setError(null);
    setBusy(kind);
    try {
      await fn();
    } catch (e: any) {
      // Ignore user-cancelled popups silently
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') {
        setBusy(null);
        return;
      }
      setError(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    run('email', () => loginWithEmail(email.trim(), password));
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #1B263B 50%, #415A77 100%)' }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-8 text-center">
          <img src="/favicon.png" alt="Flock" className="mx-auto mb-3 h-12 w-12" />
          <h1 className="text-2xl font-semibold text-flock-navy-900">Welcome to Flock</h1>
          <p className="mt-1 text-sm text-flock-navy-400">Sign in to your church community</p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => run('google', loginWithGoogle)}
            className="min-h-touch flex w-full items-center justify-center gap-3 rounded-lg border border-flock-gray-300 bg-white px-4 py-2.5 font-medium text-flock-navy-800 transition hover:bg-flock-navy-50 disabled:opacity-60"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.16-3.16C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"
              />
            </svg>
            {busy === 'google' ? 'Signing in…' : 'Continue with Google'}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => run('apple', loginWithApple)}
            className="min-h-touch flex w-full items-center justify-center gap-3 rounded-lg bg-black px-4 py-2.5 font-medium text-white transition hover:bg-flock-navy-900 disabled:opacity-60"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.86-3.08.38-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.38C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            {busy === 'apple' ? 'Signing in…' : 'Continue with Apple'}
          </button>
        </div>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-flock-gray-200" />
          <span className="text-xs uppercase tracking-wide text-flock-navy-300">or</span>
          <div className="h-px flex-1 bg-flock-gray-200" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-flock-navy-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-flock-gray-300 px-4 py-2.5 text-flock-navy-900 outline-none focus:border-flock-navy-600 focus:ring-2 focus:ring-flock-navy-600/20"
              placeholder="you@church.org"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-medium text-flock-navy-700"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-flock-gray-300 px-4 py-2.5 text-flock-navy-900 outline-none focus:border-flock-navy-600 focus:ring-2 focus:ring-flock-navy-600/20"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={busy !== null}
            className="min-h-touch w-full rounded-lg bg-flock-navy-600 px-4 py-2.5 font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-60"
          >
            {busy === 'email' ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-flock-navy-400">
          <Link to="/forgot-password" className="text-flock-navy-600 hover:underline">
            Forgot password?
          </Link>
        </div>
        <p className="mt-4 text-center text-xs text-flock-navy-300">
          New to Flock? Get the app to create your account and join your church.
        </p>
        {/* Official store badges — same artwork as website/src/app/download */}
        <div className="mt-3">
          <StoreBadges />
        </div>
      </div>
    </div>
  );
}
